import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  type AnalyzeStructureResponse,
  type DraftItem,
  type GeneratedVariant,
  type ReferenceGenerateResponse,
  type TemplateItem,
  type VoiceProfile,
} from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import styles from './home.module.css'

export const Route = createFileRoute('/workspace')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: WorkspaceRoute,
})

type GenerationMode = 'create' | 'adapt'

const QUICK_START_PROMPTS = [
  'How we cut onboarding time-to-value in half.',
  'The pricing experiment that increased annual upgrades by 27%.',
  'Paste a high-performing reference post and adapt it to your own topic.',
]

function detectMode(input: string): GenerationMode {
  const trimmed = input.trim()
  if (trimmed.length === 0) {
    return 'create'
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return 'create'
  }

  if (
    trimmed.length > 220 ||
    trimmed.split('\n').filter((line) => line.trim().length > 0).length >= 3
  ) {
    return 'adapt'
  }

  return 'create'
}

function toTitleCase(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function resolveTemplateMode(template: TemplateItem): GenerationMode {
  const mode = template.structurePayload['fromMode']
  return mode === 'adapt' ? 'adapt' : 'create'
}

function buildTemplateDraftScaffold(template: TemplateItem): string {
  const tagLine = template.tags.length > 0 ? template.tags.join(', ') : 'insight'
  return [
    `Template: ${template.name}`,
    `Focus tags: ${tagLine}`,
    '',
    'Hook:',
    'Signal or proof:',
    'Point of view:',
    'Action takeaway:',
    'CTA:',
  ].join('\n')
}

function WorkspaceRoute() {
  const [input, setInput] = useState('')
  const [topic, setTopic] = useState('')
  const [modeOverride, setModeOverride] = useState<GenerationMode | null>(null)
  const inferredMode = useMemo(() => detectMode(input), [input])
  const activeMode = modeOverride ?? inferredMode

  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([])
  const [selectedVoiceProfileId, setSelectedVoiceProfileId] = useState<string | null>(null)
  const [isCreatingVoiceProfile, setIsCreatingVoiceProfile] = useState(false)
  const [newVoiceProfileName, setNewVoiceProfileName] = useState('')
  const [newVoiceProfileDefault, setNewVoiceProfileDefault] = useState(false)
  const [voiceProfileError, setVoiceProfileError] = useState<string | null>(null)
  const [isVoiceProfileBusy, setIsVoiceProfileBusy] = useState(false)

  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [templateName, setTemplateName] = useState('')
  const [templateTags, setTemplateTags] = useState('')
  const [templateError, setTemplateError] = useState<string | null>(null)

  const [variants, setVariants] = useState<GeneratedVariant[] | null>(null)
  const [activeVariantIndex, setActiveVariantIndex] = useState(0)
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [analysisSummary, setAnalysisSummary] = useState<AnalyzeStructureResponse | null>(null)
  const [extensionNotice, setExtensionNotice] = useState<string | null>(null)
  const [loadedExtensionAnalysisId, setLoadedExtensionAnalysisId] = useState<string | null>(null)
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null)

  const [drafts, setDrafts] = useState<DraftItem[]>([])
  const [draftActionBusyKey, setDraftActionBusyKey] = useState<string | null>(null)
  const [draftActionError, setDraftActionError] = useState<string | null>(null)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isTemplatesBusy, setIsTemplatesBusy] = useState(false)
  const [activeTemplate, setActiveTemplate] = useState<TemplateItem | null>(null)
  const editorRef = useRef<HTMLFormElement | null>(null)
  const editorInputRef = useRef<HTMLTextAreaElement | null>(null)

  const scrollToEditor = (): void => {
    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      editorInputRef.current?.focus({ preventScroll: true })
    })
  }

  const loadWorkspaceState = async (): Promise<void> => {
    const [profiles, templateData, draftData] = await Promise.all([
      api.listVoiceProfiles(),
      api.listTemplates(),
      api.listDrafts({ limit: 8 }),
    ])

    setVoiceProfiles(profiles.items)
    setTemplates(templateData.items)
    setDrafts(draftData.items)

    if (profiles.items.length > 0 && !selectedVoiceProfileId) {
      const defaultProfile =
        profiles.items.find((profile) => profile.isDefault) ?? profiles.items[0]
      setSelectedVoiceProfileId(defaultProfile?.id ?? null)
    }
  }

  useEffect(() => {
    const bootstrap = async (): Promise<void> => {
      try {
        await loadWorkspaceState()
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load workspace.')
      }
    }

    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadExtensionHandoff = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search)
      const source = params.get('source')
      const analysisId = params.get('analysisId')
      const extensionError = params.get('error')

      if (source !== 'extension') {
        return
      }

      if (extensionError === 'extraction_failed') {
        setExtensionNotice(
          'Extension extraction failed. Paste the reference text manually to continue.'
        )
        return
      }

      if (!analysisId || analysisId === loadedExtensionAnalysisId) {
        return
      }

      try {
        const analysis = await api.getAnalysis(analysisId)
        setInput(analysis.referenceText)
        setTopic((current) =>
          current.length > 0 ? current : 'Apply this structure to my current strategy update'
        )
        setModeOverride('adapt')
        setAnalysisSummary({
          analysisId: analysis.analysisId,
          hookType: analysis.hookType,
          pacing: analysis.pacing,
          payoffType: analysis.payoffType,
          whyItWorks: analysis.whyItWorks,
        })
        setExtensionNotice('Reference loaded. Add your topic and generate an adapted draft.')
        setLoadedExtensionAnalysisId(analysisId)
      } catch (error) {
        setExtensionNotice(
          error instanceof Error ? error.message : 'Failed to load extension context.'
        )
      }
    }

    void loadExtensionHandoff()
  }, [loadedExtensionAnalysisId])

  const handleGenerate = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    if (input.trim().length === 0) {
      setErrorMessage('Add an idea, URL, or reference text first.')
      return
    }

    if (activeMode === 'adapt' && topic.trim().length === 0) {
      setErrorMessage('Add the target topic for this adapted draft.')
      return
    }

    setIsGenerating(true)
    setErrorMessage(null)

    try {
      if (activeMode === 'create') {
        const response = await api.generateCreate({
          input: input.trim(),
          inputType: 'auto',
          platform: 'linkedin',
          ...(selectedVoiceProfileId ? { voiceProfileId: selectedVoiceProfileId } : {}),
        })

        setVariants(response.variants)
        setActiveVariantIndex(0)
        setGenerationId(response.generationId)
        setAnalysisSummary(null)
      } else {
        const analysis = await api.analyzeReferenceStructure({
          sourceType: 'paste',
          referenceText: input.trim(),
        })

        const adaptedResponse: ReferenceGenerateResponse = await api.generateFromReference({
          analysisId: analysis.analysisId,
          topic: topic.trim(),
          platform: 'linkedin',
          ...(selectedVoiceProfileId ? { voiceProfileId: selectedVoiceProfileId } : {}),
        })

        setVariants(adaptedResponse.variants)
        setActiveVariantIndex(0)
        setGenerationId(adaptedResponse.generationId)
        setAnalysisSummary(analysis)
      }

      const draftData = await api.listDrafts({ limit: 8 })
      setDrafts(draftData.items)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Generation failed.')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCreateTemplate = async (): Promise<void> => {
    setTemplateError(null)

    if (templateName.trim().length === 0) {
      setTemplateError('Template name is required.')
      return
    }

    setIsTemplatesBusy(true)

    try {
      const tags = templateTags
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)

      await api.createTemplate({
        name: templateName.trim(),
        tags,
        structurePayload: {
          fromMode: activeMode,
          generatedFrom: generationId,
        },
      })

      setTemplateName('')
      setTemplateTags('')

      const templateData = await api.listTemplates()
      setTemplates(templateData.items)
    } catch (error) {
      setTemplateError(error instanceof Error ? error.message : 'Template creation failed.')
    } finally {
      setIsTemplatesBusy(false)
    }
  }

  const handleCreateVoiceProfile = async (): Promise<void> => {
    setVoiceProfileError(null)

    if (newVoiceProfileName.trim().length === 0) {
      setVoiceProfileError('Profile name is required.')
      return
    }

    setIsVoiceProfileBusy(true)

    try {
      const created = await api.createVoiceProfile({
        name: newVoiceProfileName.trim(),
        ...(newVoiceProfileDefault ? { isDefault: true } : {}),
      })

      const profileData = await api.listVoiceProfiles()
      setVoiceProfiles(profileData.items)
      setSelectedVoiceProfileId(created.id)
      setNewVoiceProfileName('')
      setNewVoiceProfileDefault(false)
      setIsCreatingVoiceProfile(false)
      setExtensionNotice(`Voice profile created: ${created.name}.`)
    } catch (error) {
      setVoiceProfileError(
        error instanceof Error ? error.message : 'Voice profile creation failed.'
      )
    } finally {
      setIsVoiceProfileBusy(false)
    }
  }

  const handleDeleteTemplate = async (templateId: string): Promise<void> => {
    setIsTemplatesBusy(true)
    setTemplateError(null)

    try {
      await api.deleteTemplate(templateId)
      const templateData = await api.listTemplates()
      setTemplates(templateData.items)
    } catch (error) {
      setTemplateError(error instanceof Error ? error.message : 'Template deletion failed.')
    } finally {
      setIsTemplatesBusy(false)
    }
  }

  const handleResumeDraft = (draft: DraftItem): void => {
    setInput(draft.content)
    setModeOverride(detectMode(draft.content))
    setTopic((current) => {
      if (current.trim().length > 0) {
        return current
      }
      return draft.title ?? ''
    })
    setVariants(null)
    setGenerationId(null)
    setAnalysisSummary(null)
    setActiveDraftId(draft.id)
    setErrorMessage(null)
    setExtensionNotice(
      `Draft loaded: ${draft.title ?? 'Untitled draft'}. Continue editing and generate new variants.`
    )
    scrollToEditor()
  }

  const handleApplyTemplate = (template: TemplateItem): void => {
    const resolvedMode = resolveTemplateMode(template)
    const templateScaffold = buildTemplateDraftScaffold(template)

    setModeOverride(resolvedMode)
    setInput(templateScaffold)
    if (resolvedMode === 'adapt') {
      const fallbackTopic = `Apply ${template.name} to this week’s primary audience pain point`
      setTopic((current) => (current.trim().length > 0 ? current : fallbackTopic))
    }

    setActiveTemplate(template)
    setExtensionNotice(`Template loaded: ${template.name}. Fill in specifics and generate.`)
    setErrorMessage(null)
    scrollToEditor()
  }

  const handleRotateVariant = (direction: 'prev' | 'next'): void => {
    if (!variants || variants.length === 0) {
      return
    }

    setActiveVariantIndex((current) => {
      if (direction === 'prev') {
        return (current - 1 + variants.length) % variants.length
      }
      return (current + 1) % variants.length
    })
  }

  const refreshDrafts = async (): Promise<void> => {
    const draftData = await api.listDrafts({ limit: 8 })
    setDrafts(draftData.items)
  }

  const handlePostDraft = async (draft: DraftItem): Promise<void> => {
    const busyKey = `post:${draft.id}`
    setDraftActionBusyKey(busyKey)
    setDraftActionError(null)

    try {
      await api.publishDraft(draft.id)
      await refreshDrafts()
      setExtensionNotice(`Posted: ${draft.title ?? 'Untitled draft'}.`)
    } catch (error) {
      setDraftActionError(error instanceof Error ? error.message : 'Failed to post draft.')
    } finally {
      setDraftActionBusyKey(null)
    }
  }

  const handleAddImageToDraft = async (draft: DraftItem): Promise<void> => {
    const busyKey = `image:${draft.id}`
    setDraftActionBusyKey(busyKey)
    setDraftActionError(null)

    try {
      const image = await api.generateDraftImage({
        prompt: draft.content,
        aspectRatio: '1:1',
      })
      await api.updateDraft(draft.id, {
        imageUrl: image.imageUrl,
      })
      await refreshDrafts()
      setExtensionNotice(`Image added via ${image.provider}: ${draft.title ?? 'Untitled draft'}.`)
    } catch (error) {
      setDraftActionError(error instanceof Error ? error.message : 'Failed to generate image.')
    } finally {
      setDraftActionBusyKey(null)
    }
  }

  const canShowTemplateBuilder = variants !== null

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <p className={styles.overline}>Workspace</p>
        <h1 className={styles.pageTitle}>Create in your voice and ship this week&apos;s queue</h1>
        <p className={styles.subtitle}>
          Build from a rough idea or adapt a proven structure. Keep output aligned to your profile,
          then move the best version into your draft pipeline.
        </p>
      </header>

      {extensionNotice && <p className={styles.notice}>{extensionNotice}</p>}

      {activeTemplate && (
        <p className={styles.notice}>
          Active template: <strong>{activeTemplate.name}</strong>{' '}
          <button
            type="button"
            className={styles.templateClearButton}
            onClick={() => setActiveTemplate(null)}
          >
            Clear
          </button>
        </p>
      )}

      <section className={styles.quickStartCard}>
        <h2>Start with a prompt</h2>
        <p className={styles.muted}>Pick one and run your first pass immediately.</p>
        <div className={styles.quickStartButtons}>
          {QUICK_START_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className={styles.quickStartButton}
              onClick={() => {
                setInput(prompt)
                setModeOverride(detectMode(prompt))
                setErrorMessage(null)
                scrollToEditor()
              }}
            >
              {prompt}
            </button>
          ))}
        </div>
      </section>

      <form ref={editorRef} className={styles.form} onSubmit={handleGenerate}>
        <div className={styles.modeRow}>
          <span className={styles.modeLabel}>Detected mode:</span>
          <strong>{toTitleCase(inferredMode)}</strong>
          <div className={styles.modeButtons}>
            <button
              type="button"
              className={activeMode === 'create' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => setModeOverride('create')}
            >
              Create
            </button>
            <button
              type="button"
              className={activeMode === 'adapt' ? styles.modeButtonActive : styles.modeButton}
              onClick={() => setModeOverride('adapt')}
            >
              Adapt
            </button>
            <button
              type="button"
              className={styles.modeButton}
              onClick={() => setModeOverride(null)}
            >
              Auto
            </button>
          </div>
        </div>

        <div className={styles.voiceProfileBlock}>
          <div className={styles.voiceProfileHeader}>
            <label htmlFor="voice-profile-select" className={styles.voiceProfileTitle}>
              Voice profile
            </label>
            <button
              type="button"
              className={styles.inlineLinkButton}
              onClick={() => {
                setIsCreatingVoiceProfile((current) => !current)
                setVoiceProfileError(null)
              }}
            >
              {isCreatingVoiceProfile ? 'Close profile creator' : 'Create new profile'}
            </button>
          </div>

          <select
            id="voice-profile-select"
            className={styles.voiceProfileSelect}
            value={selectedVoiceProfileId ?? ''}
            onChange={(event) => setSelectedVoiceProfileId(event.target.value || null)}
          >
            {voiceProfiles.length === 0 && <option value="">No profiles yet</option>}
            {voiceProfiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name} ({profile.quality})
              </option>
            ))}
          </select>

          <p className={styles.voiceProfileHint}>
            Need a separate writing style? Click <strong>Create new profile</strong>, name it, and
            save.
          </p>

          {isCreatingVoiceProfile && (
            <div className={styles.voiceProfileCreate}>
              <input
                type="text"
                value={newVoiceProfileName}
                onChange={(event) => setNewVoiceProfileName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void handleCreateVoiceProfile()
                  }
                }}
                placeholder="e.g. Founder narrative"
              />

              <label className={styles.voiceProfileDefaultToggle}>
                <input
                  type="checkbox"
                  checked={newVoiceProfileDefault}
                  onChange={(event) => setNewVoiceProfileDefault(event.target.checked)}
                />
                Set as default profile
              </label>

              <div className={styles.voiceProfileCreateActions}>
                <button
                  type="button"
                  className={styles.templateUseButton}
                  onClick={() => {
                    void handleCreateVoiceProfile()
                  }}
                  disabled={isVoiceProfileBusy}
                >
                  {isVoiceProfileBusy ? 'Creating...' : 'Save profile'}
                </button>
                <button
                  type="button"
                  className={styles.modeButton}
                  onClick={() => {
                    setIsCreatingVoiceProfile(false)
                    setVoiceProfileError(null)
                  }}
                  disabled={isVoiceProfileBusy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {voiceProfileError && <p className={styles.error}>{voiceProfileError}</p>}
        </div>

        <label className={styles.inputLabel}>
          {activeMode === 'create' ? 'Idea, URL, or source text' : 'Reference text to adapt'}
          <textarea
            ref={editorInputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={
              activeMode === 'create'
                ? 'e.g. Lessons from rebuilding onboarding'
                : 'Paste a high-performing post to model structure'
            }
            rows={7}
          />
        </label>

        {activeMode === 'adapt' && (
          <label className={styles.inputLabel}>
            Target topic
            <input
              type="text"
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="e.g. What we learned reducing churn in our onboarding flow"
            />
          </label>
        )}

        <button type="submit" className={styles.generateButton} disabled={isGenerating}>
          {isGenerating
            ? 'Generating...'
            : activeMode === 'create'
              ? 'Generate drafts'
              : 'Generate adapted drafts'}
        </button>

        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </form>

      {analysisSummary && (
        <section className={styles.analysisCard}>
          <h2>Reference structure summary</h2>
          <p>
            <strong>Hook:</strong> {toTitleCase(analysisSummary.hookType)}
          </p>
          <p>
            <strong>Pacing:</strong> {toTitleCase(analysisSummary.pacing)}
          </p>
          <p>
            <strong>Payoff:</strong> {toTitleCase(analysisSummary.payoffType)}
          </p>
          <p>{analysisSummary.whyItWorks}</p>
        </section>
      )}

      {variants && (
        <section className={styles.results}>
          <div className={styles.resultsHeader}>
            <h2>Draft variants</h2>
            <p>
              Generation <code>{generationId}</code>
            </p>
          </div>

          <div className={styles.variantCarouselHeader}>
            <button
              type="button"
              className={styles.modeButton}
              onClick={() => handleRotateVariant('prev')}
              disabled={variants.length <= 1}
            >
              Previous
            </button>
            <p className={styles.muted}>
              Variant {activeVariantIndex + 1} of {variants.length}
            </p>
            <button
              type="button"
              className={styles.modeButton}
              onClick={() => handleRotateVariant('next')}
              disabled={variants.length <= 1}
            >
              Next
            </button>
          </div>

          <div className={styles.variantGrid}>
            {variants[activeVariantIndex] && (
              <article key={variants[activeVariantIndex].id} className={styles.variantCard}>
                <h3>{variants[activeVariantIndex].label}</h3>
                <p className={styles.variantBody}>{variants[activeVariantIndex].content}</p>
                {variants[activeVariantIndex].scores && (
                  <div className={styles.scoreRow}>
                    <span>Voice {variants[activeVariantIndex].scores?.voiceMatch}</span>
                    <span>Readability {variants[activeVariantIndex].scores?.readability}</span>
                    <span>Novelty {variants[activeVariantIndex].scores?.novelty}</span>
                  </div>
                )}
              </article>
            )}
          </div>

          <div className={styles.variantTabs}>
            {variants.map((variant, index) => (
              <button
                key={variant.id}
                type="button"
                className={
                  index === activeVariantIndex ? styles.modeButtonActive : styles.modeButton
                }
                onClick={() => setActiveVariantIndex(index)}
              >
                {variant.label}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className={styles.draftsCard}>
        <div className={styles.templatesHeader}>
          <h2>Recent drafts</h2>
          <p>{drafts.length} items</p>
        </div>
        <p className={styles.muted}>Pick a draft to continue from where you left off.</p>
        {draftActionError && <p className={styles.error}>{draftActionError}</p>}
        <div className={styles.templateList}>
          {drafts.map((draft) => (
            <article
              key={draft.id}
              className={`${styles.templateRow} ${
                activeDraftId === draft.id ? styles.templateRowSelected : ''
              }`}
            >
              <div className={styles.draftSummary}>
                {draft.imageUrl && (
                  <img
                    src={draft.imageUrl}
                    alt={`Visual for ${draft.title ?? 'draft'}`}
                    className={styles.draftImage}
                  />
                )}
                <h3>{draft.title ?? 'Untitled draft'}</h3>
                <p>
                  {draft.status} • {draft.platform} • {new Date(draft.updatedAt).toLocaleString()}
                </p>
              </div>
              <div className={styles.templateActions}>
                <button
                  type="button"
                  className={styles.templateUseButton}
                  onClick={() => handleResumeDraft(draft)}
                >
                  {activeDraftId === draft.id ? 'Editing' : 'Edit draft'}
                </button>
                <button
                  type="button"
                  className={styles.templateUseButton}
                  onClick={() => {
                    void handleAddImageToDraft(draft)
                  }}
                  disabled={draftActionBusyKey === `image:${draft.id}`}
                >
                  {draftActionBusyKey === `image:${draft.id}` ? 'Adding image...' : 'Add image'}
                </button>
                <button
                  type="button"
                  className={styles.templateUseButton}
                  onClick={() => {
                    void handlePostDraft(draft)
                  }}
                  disabled={
                    draft.status === 'published' || draftActionBusyKey === `post:${draft.id}`
                  }
                >
                  {draft.status === 'published'
                    ? 'Posted'
                    : draftActionBusyKey === `post:${draft.id}`
                      ? 'Posting...'
                      : 'Post now'}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.templatesCard}>
        <div className={styles.templatesHeader}>
          <h2>Reusable templates</h2>
          <p>{templates.length} total</p>
        </div>
        <p className={styles.muted}>
          Click <strong>Use template</strong> to load it into the composer and generate immediately.
        </p>

        {canShowTemplateBuilder && (
          <div className={styles.templateBuilder}>
            <input
              type="text"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="Template name"
            />
            <input
              type="text"
              value={templateTags}
              onChange={(event) => setTemplateTags(event.target.value)}
              placeholder="Tags (comma-separated)"
            />
            <button type="button" onClick={handleCreateTemplate} disabled={isTemplatesBusy}>
              Save template
            </button>
          </div>
        )}

        {templateError && <p className={styles.error}>{templateError}</p>}

        <div className={styles.templateList}>
          {templates.map((template) => (
            <article key={template.id} className={styles.templateRow}>
              <div>
                <h3>{template.name}</h3>
                <p>
                  {template.visibility}
                  {template.tags.length > 0 ? ` • ${template.tags.join(', ')}` : ''}
                </p>
              </div>
              <div className={styles.templateActions}>
                <button
                  type="button"
                  className={styles.templateUseButton}
                  onClick={() => handleApplyTemplate(template)}
                >
                  Use template
                </button>
                {template.visibility === 'user' && (
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => {
                      void handleDeleteTemplate(template.id)
                    }}
                    disabled={isTemplatesBusy}
                  >
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  )
}
